/**
 * QueueHealthPage — /queues
 *
 * US-24-GR-13 — Queue depth tiles, DLQ sparkline, and recent dead-letters table.
 * US-24-GR-05 — Redrive button per DLQ row with confirmation modal.
 * Polls every 30 seconds, pauses when the tab is hidden.
 */

import { useState, useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getQueues,
  redriveDlqMessage,
  type QueuesResponse,
  type DeadLetterDto,
  type DlqDepthPoint,
  type AlertThresholdConfig,
} from '../api/orchestratorClient';
import { usePolledQuery } from '../hooks/usePolledQuery';
import { Sparkline } from '../components/Sparkline';
import { DlqRedriveModal } from '../components/DlqRedriveModal';
import { describeRedriveFailure } from './redriveFailureMessage';
import { Toast, type ToastState } from '../components/Toast';
import { AlertThresholdsPanel } from '../components/AlertThresholdsPanel';
import { AlertsList } from '../components/AlertsList';
import { AbbrLabel } from '../components/AbbrLabel';
import { useAdminHub } from '../contexts/AdminHubContext';
import {
  type Threshold,
  queueThreshold,
  dlqThreshold,
  computeEventsQueueThreshold,
  computeEventsDlqThreshold,
} from './queueThresholds';

// ---------------------------------------------------------------------------
// Threshold helpers
//
// Defaults mirror the pre-US-24-GR-27 hardcoded values — used while the
// alert-thresholds config is loading or unavailable so the tiles never go
// unthemed.
// ---------------------------------------------------------------------------

const DEFAULT_THRESHOLDS: AlertThresholdConfig = {
  queueDepthWarn: 10,
  queueDepthAlert: 50,
  dlqDepthWarn: 0,
  dlqDepthAlert: 5,
  eventsQueueDepthWarn: 10,
  eventsQueueDepthAlert: 50,
  eventsDlqDepthWarn: 0,
  eventsDlqDepthAlert: 5,
  successRateFloorPct: 90,
  windowMinutes: 60,
};

// US-24-GR-39: the threshold helpers live in ./queueThresholds so tests can import the real
// implementation rather than re-declaring their own copy. They are deliberately NOT re-exported
// from here — exporting non-components from a page file trips react-refresh/only-export-components.

const THRESHOLD_CLASSES: Record<Threshold, string> = {
  healthy: 'border-emerald-500/30 bg-emerald-500/5',
  warn:    'border-amber-500/30 bg-amber-500/5',
  alert:   'border-red-500/30 bg-red-500/5',
};

const THRESHOLD_VALUE_CLASSES: Record<Threshold, string> = {
  healthy: 'text-emerald-400',
  warn:    'text-amber-400',
  alert:   'text-red-400',
};

// ---------------------------------------------------------------------------
// DLQ history window disclosure (US-24-GR-31, corrected by US-24-GR-40 / 16236)
//
// The DLQ sparkline is backed by a 240-point Redis ring buffer — it is not
// durable history. GR-31 stopped asserting a fixed "last 6 hours" and computed
// the covered window from the timestamps actually present. Two things that fix
// left wrong, both verified against the API on 2026-09-02:
//
// 1. The static fallback still said "last 6 hours (approx.)". Six hours is the
//    Redis key's TTL (AdminOrchestratorService.cs, DlqHistoryTtl), NOT the span
//    the samples cover. 240 points at this page's 30s poll is about 2 hours, so
//    the caption overstated coverage three- to four-fold — and it is the one
//    caption a reader cannot check against the data. It is gone: when the
//    timestamps cannot be read, the honest answer is that the window is unknown.
//
// 2. Nothing samples unless an operator is watching. GetQueuesAsync has exactly
//    one caller — AdminOrchestratorController — and there is no hosted service
//    or timer behind it. A point is appended only while somebody has this page
//    open, so "last 2 hours 15m" reads as continuous coverage of a period that
//    may be almost entirely unsampled, and the sparkline draws a straight line
//    across the hole. A DLQ spike at 03:00 with nobody watching leaves no mark
//    and the label afterwards still appears to cover it: the chart's failure
//    mode looks like good news, which is the exact thing the parent story
//    exists to prevent.
//
// So the span is still reported, and it now carries how it was collected, plus
// any gap large enough to mean nobody was looking.
// ---------------------------------------------------------------------------

/** Matches the usePolledQuery interval below — a gap is only meaningful against it. */
const DLQ_POLL_INTERVAL_MS = 30_000;

/**
 * Six polls, three minutes. Browsers throttle timers hard in a background tab,
 * so a series that merely stretches is a watched page, not an unwatched one.
 * Crying gap at every stretched interval would train the reader to ignore the
 * disclosure, which costs more than the short holes it would catch.
 *
 * Measured on DEV (work item 16843, 2026-10-23, 196 samples over 2 h 10 min):
 * with the page held open the intervals reached 140 s and 155 s, so the old
 * four-poll (120 s) value reported two false gaps. A real step-away was 633 s.
 */
const DLQ_GAP_THRESHOLD_MS = DLQ_POLL_INTERVAL_MS * 6;

/** Said in every caption that reports a span: the span is not coverage. */
const SAMPLING_CAVEAT = 'sampled only while this page is open';

function formatDlqHistoryDuration(spanMs: number): string {
  const totalMinutes = Math.round(spanMs / 60_000);
  if (totalMinutes < 1) return 'under a minute';
  if (totalMinutes === 1) return '1 minute';
  if (totalMinutes < 60) return `${totalMinutes} minutes`;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const hoursLabel = `${hours} hour${hours === 1 ? '' : 's'}`;
  if (minutes === 0) return hoursLabel;
  return `${hoursLabel} ${minutes}m`;
}

/** Every interval between consecutive samples that is long enough to be a real hole. */
function findSamplingGaps(sortedTimestamps: number[]): number[] {
  const gaps: number[] = [];
  for (let i = 1; i < sortedTimestamps.length; i += 1) {
    const gap = sortedTimestamps[i] - sortedTimestamps[i - 1];
    if (gap > DLQ_GAP_THRESHOLD_MS) gaps.push(gap);
  }
  return gaps;
}

/** Returns null when there is no history at all — the empty-state copy already covers that case. */
function formatDlqHistoryWindow(history: DlqDepthPoint[]): string | null {
  if (history.length === 0) return null;

  const timestamps = history
    .map((p) => new Date(p.timestamp).getTime())
    .filter((t) => Number.isFinite(t))
    .sort((a, b) => a - b);

  if (timestamps.length === 0) {
    // 16236: was 'last 6 hours (approx.)'. Naming a span we cannot see is worse
    // than naming none — the reader has no way to catch it.
    return 'sample times unreadable — window unknown';
  }

  const spanMs = timestamps[timestamps.length - 1] - timestamps[0];
  if (spanMs <= 0) {
    return history.length === 1
      ? 'first sample just recorded'
      : 'samples from a single refresh so far';
  }

  const span = formatDlqHistoryDuration(spanMs);
  const gaps = findSamplingGaps(timestamps);
  if (gaps.length === 0) return `last ${span}, ${SAMPLING_CAVEAT}`;

  const longest = formatDlqHistoryDuration(Math.max(...gaps));
  const count = gaps.length === 1 ? '1 gap' : `${gaps.length} gaps`;
  return `last ${span}, but ${count} in it went unsampled (longest ${longest}) — ${SAMPLING_CAVEAT}`;
}

// ---------------------------------------------------------------------------
// Dead-letter panel disclosure (US-24-GR-41 / 16237)
//
// The panel made three claims the data does not support. All three verified
// against the API on 2026-09-02:
//
// 1. "No dead-lettered messages in the last 24 hours." Nothing filters by time.
//    PeekDlqMessagesAsync -> PeekMessagesAsync returns whatever is sitting in
//    the dead-letter subqueue right now, of any age. A dead letter from last
//    week is listed; a queue drained an hour ago reported nothing in 24 hours.
//
// 2. The panel headed "Recent" showed the OLDEST messages. Service Bus peeks
//    from the front of the queue, so the service receives the oldest 25 per
//    queue, then sorts descending and takes 25 — a sort that changes the
//    display order but cannot change which 25 were fetched. On a large DLQ,
//    exactly when it matters most, the newest dead letters are not listed and
//    nothing said so.
//
// 3. A failed peek rendered as an empty DLQ. The provider catches every
//    exception, logs a warning and returns the list it has (AzureServiceBus-
//    MetricsProvider.cs:94-98), so a failure arrives indistinguishable from an
//    empty queue — the absence-versus-failure defect the sparkline section of
//    this same file was fixed for, twelve lines above.
//
// The depth counters come from a different call (GetDlqDepthAsync) and are
// already in this response, so the UI can catch (3) itself: a non-zero depth
// beside an empty list is not an empty DLQ. It cannot catch a failed peek on a
// genuinely empty queue, and it must not pretend to — only the API can report
// that, and it still swallows the exception (follow-up filed).
// ---------------------------------------------------------------------------

type DeadLetterDisclosure =
  | { kind: 'empty' }
  | { kind: 'unreadable'; shown: number }
  | { kind: 'unlistable'; depth: number }
  | { kind: 'complete'; shown: number }
  | { kind: 'partial'; shown: number; depth: number };

// Work item 16357: the server now says outright whether the peek failed (16305's
// `deadLetterReadFailed` / `deadLetterReadPartial`), so the two claims below stop being
// inferences.
//
// The inference from 16237 is KEPT, not replaced, and the order matters. The explicit flag is
// checked first because it is the stronger signal and covers the case 16237's own comment said it
// could not: a failed peek on a genuinely EMPTY queue, where depth is 0 and there is nothing to
// notice. The depth-versus-empty-list inference then still catches a response from an API that
// predates 16305 and omits the flags. Neither double-reports, because they cannot both win.
function describeDeadLetters(
  shown: number,
  tasksDlqDepth: number,
  eventsDlqDepth: number,
  readFailed = false,
  readPartial = false,
): DeadLetterDisclosure {
  const depth = tasksDlqDepth + eventsDlqDepth;

  // The server told us the read threw. Whatever is listed is unreliable, however much of it there
  // is, and no arithmetic on depth can improve on that.
  if (readFailed) return { kind: 'unreadable', shown };

  // A prefix of a longer list: the entries are real, there are simply more. Reported even when
  // depth happens to agree with `shown`, because the server observed the truncation directly and
  // the depth comparison is a separate, staler call.
  if (readPartial) return { kind: 'partial', shown, depth: Math.max(depth, shown + 1) };

  if (shown === 0) {
    // Depth and peek are separate calls, so they can disagree by a message when
    // a queue drains between them. Depth > 0 with nothing listed is the shape a
    // swallowed peek failure takes, and saying "none" here is the bug.
    return depth > 0 ? { kind: 'unlistable', depth } : { kind: 'empty' };
  }
  // shown > depth happens when the DLQ drains between the two calls. That is a
  // stale count, not a hidden remainder — do not invent a shortfall from it.
  return shown >= depth ? { kind: 'complete', shown } : { kind: 'partial', shown, depth };
}

/** The parenthetical beside the heading: "(25 of 137)" when some are hidden. */
function deadLetterCountLabel(d: DeadLetterDisclosure): string {
  if (d.kind === 'complete') return ` (${d.shown})`;
  if (d.kind === 'partial') return ` (${d.shown} of ${d.depth})`;
  return '';
}

// ---------------------------------------------------------------------------
// Depth tile
// ---------------------------------------------------------------------------

interface DepthTileProps {
  label: ReactNode;
  value: number | null;
  threshold: Threshold;
  isError?: boolean;
}

function DepthTile({ label, value, threshold, isError = false }: DepthTileProps) {
  const borderBg = isError
    ? 'border-zinc-700 bg-zinc-900'
    : THRESHOLD_CLASSES[threshold];
  const valueColor = isError
    ? 'text-zinc-500'
    : THRESHOLD_VALUE_CLASSES[threshold];

  return (
    <div
      className={`flex flex-col gap-1 p-4 rounded-xl border min-h-[80px] ${borderBg}`}
    >
      <span className="text-xs text-zinc-400 font-medium">{label}</span>
      <span className={`text-2xl font-bold ${valueColor}`}>
        {isError ? '—' : (value === null ? '—' : value.toLocaleString())}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Redrive target state
// ---------------------------------------------------------------------------

interface RedriveTarget {
  queue: 'agent-tasks' | 'agent-events';
  messageId: string;
  deadLetterReason: string;
  payloadPreview: string;
}

// ---------------------------------------------------------------------------
// Dead-letter row — expandable + Redrive button
// ---------------------------------------------------------------------------

interface DeadLetterRowProps {
  msg: DeadLetterDto;
  onRedriveClick: (target: RedriveTarget) => void;
}

function DeadLetterRow({ msg, onRedriveClick }: DeadLetterRowProps) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  function tryNavigateToJob() {
    if (msg.jobId) {
      navigate(`/jobs?jobId=${encodeURIComponent(msg.jobId)}`);
    }
  }

  function handleRedriveClick(e: React.MouseEvent) {
    e.stopPropagation();
    const queue = (msg.queue === 'agent-tasks' || msg.queue === 'agent-events')
      ? msg.queue
      : 'agent-tasks';
    onRedriveClick({
      queue,
      messageId: msg.messageId,
      deadLetterReason: msg.deadLetterReason ?? '',
      payloadPreview: msg.bodyPreview,
    });
  }

  return (
    <li className="border-b border-zinc-800/50 last:border-0">
      <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-800/40 transition-colors min-h-[48px]">
        {/* Expand toggle — click on the row body */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded === true}
          aria-label={`Toggle details for message dead-lettered at ${msg.deadLetteredAt}`}
          className="flex items-center gap-3 flex-1 min-w-0 text-left"
        >
          <time
            dateTime={msg.deadLetteredAt}
            className="text-xs font-mono text-zinc-500 w-36 flex-shrink-0"
          >
            {new Date(msg.deadLetteredAt).toLocaleString()}
          </time>
          <span className="text-xs bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-400 flex-shrink-0">
            {msg.queue}
          </span>
          <span className="text-xs text-zinc-300 flex-shrink-0">{msg.messageType}</span>
          {msg.jobId && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); tryNavigateToJob(); }}
              className="text-xs font-mono text-violet-400 hover:text-violet-300 flex-shrink-0"
              aria-label={`View job ${msg.jobId}`}
            >
              {msg.jobId.slice(0, 8)}…
            </button>
          )}
          <span className="flex-1 text-xs text-zinc-500 truncate text-right">
            {msg.deadLetterReason}
          </span>
        </button>

        {/* Redrive action button */}
        <button
          type="button"
          onClick={handleRedriveClick}
          aria-label={`Redrive message ${msg.messageId}`}
          className="flex-shrink-0 min-h-[48px] min-w-[48px] px-3 py-1 rounded-lg border border-violet-500/50 text-violet-400 hover:bg-violet-500/10 hover:text-violet-300 hover:border-violet-400 text-xs font-medium transition-colors"
        >
          Redrive
        </button>
      </div>

      {expanded && (
        <div className="px-4 pb-3 space-y-2">
          {msg.deadLetterErrorDescription && (
            <p className="text-xs text-zinc-400">{msg.deadLetterErrorDescription}</p>
          )}
          <pre className="text-xs text-zinc-300 bg-zinc-950 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words font-mono max-h-48">
            {msg.bodyPreview}
          </pre>
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function QueueHealthPage() {
  const { data, error, isLoading } = usePolledQuery<QueuesResponse>(getQueues, DLQ_POLL_INTERVAL_MS);
  const { events } = useAdminHub();

  const [redriveTarget, setRedriveTarget] = useState<RedriveTarget | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [thresholdConfig, setThresholdConfig] = useState<AlertThresholdConfig>(DEFAULT_THRESHOLDS);

  // Bump this whenever a live ThresholdAlert event lands so AlertsList refetches.
  const thresholdAlertCount = events.filter((e) => e.EventType === 'ThresholdAlert').length;

  const dlqHistoryPoints = data?.dlqDepthHistory ?? [];
  const dlqTasksHistory = dlqHistoryPoints.map((p) => p.tasksDlq);
  const dlqEventsHistory = dlqHistoryPoints.map((p) => p.eventsDlq);
  const dlqHistoryWindowLabel = formatDlqHistoryWindow(dlqHistoryPoints);
  const deadLetterDisclosure = describeDeadLetters(
    data?.recentDeadLetters?.length ?? 0,
    data?.tasks.dlqDepth ?? 0,
    data?.events.dlqDepth ?? 0,
    data?.deadLetterReadFailed ?? false,
    data?.deadLetterReadPartial ?? false,
  );

  const tasksDepthThreshold = data ? queueThreshold(data.tasks.depth, thresholdConfig) : 'healthy';
  const eventsDepthThreshold = data ? computeEventsQueueThreshold(data.events.depth, thresholdConfig) : 'healthy';
  const tasksDlqThreshold = data ? dlqThreshold(data.tasks.dlqDepth, thresholdConfig) : 'healthy';
  const eventsDlqThreshold = data ? computeEventsDlqThreshold(data.events.dlqDepth, thresholdConfig) : 'healthy';

  const handleDismissToast = useCallback(() => setToast(null), []);

  async function handleConfirmRedrive() {
    if (!redriveTarget) return;
    const { queue, messageId } = redriveTarget;

    try {
      const result = await redriveDlqMessage(queue, messageId);

      if (!result.success && result.errorMessage) {
        setToast({ variant: 'error', message: result.errorMessage });
      } else if (result.alreadyRedriven) {
        setToast({ variant: 'info', message: 'Already redriven — no duplicate created' });
      } else {
        setToast({ variant: 'success', message: 'Message redriven — will disappear on next poll cycle' });
      }
    } catch (err) {
      setToast({ variant: 'error', message: describeRedriveFailure(err), durationMs: 8000 });
    } finally {
      setRedriveTarget(null);
    }
  }

  return (
    <main className="flex-1 flex flex-col gap-6 overflow-y-auto px-6 py-6">
      <h1 className="text-sm font-semibold text-zinc-100">
        Queue &amp; <AbbrLabel term="DLQ" /> Health
      </h1>

      {/* Error banner — partial: tiles still show */}
      {error && (
        <div
          role="alert"
          className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-xs text-red-300"
        >
          Service Bus data unavailable: {error}
        </div>
      )}

      {/* Depth tiles */}
      <section aria-label="Queue depth counters">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <DepthTile
            label="agent-tasks depth"
            value={data?.tasks.depth ?? null}
            threshold={tasksDepthThreshold}
            // 16357: a failed depth read arrives as a placeholder 0, which sits below every
            // threshold and would paint a green tile. isError renders a neutral "—" instead.
            isError={(!!error && !data) || (data?.tasks.depthReadFailed ?? false)}
          />
          <DepthTile
            label="agent-events depth"
            value={data?.events.depth ?? null}
            threshold={eventsDepthThreshold}
            isError={(!!error && !data) || (data?.events.depthReadFailed ?? false)}
          />
          <DepthTile
            label={<>agent-tasks <AbbrLabel term="DLQ" /></>}
            value={data?.tasks.dlqDepth ?? null}
            threshold={tasksDlqThreshold}
            isError={(!!error && !data) || (data?.tasks.dlqDepthReadFailed ?? false)}
          />
          <DepthTile
            label={<>agent-events <AbbrLabel term="DLQ" /></>}
            value={data?.events.dlqDepth ?? null}
            threshold={eventsDlqThreshold}
            isError={(!!error && !data) || (data?.events.dlqDepthReadFailed ?? false)}
          />

          {/* Active worker sessions tile */}
          <div className="flex flex-col gap-1 p-4 rounded-xl border border-zinc-700 bg-zinc-900 min-h-[80px]">
            <span className="text-xs text-zinc-400 font-medium">Active Workers</span>
            {data?.activeWorkerSessions === null || data?.activeWorkerSessions === undefined ? (
              <span className="text-sm text-zinc-500">Not yet metered</span>
            ) : (
              <span className="text-2xl font-bold text-zinc-200">
                {data.activeWorkerSessions.toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Sparkline (US-24-GR-21).
          Always render the section once we have a queues response so an empty history reads as
          "No history yet" rather than silently disappearing (absence vs failure). While there is
          no data at all (initial load / hard error) the section stays hidden. */}
      {data && (
        <section aria-label="DLQ depth history sparkline">
          <p className="text-xs font-medium text-zinc-400 mb-2">
            <AbbrLabel term="DLQ" /> Depth History
            {dlqHistoryWindowLabel && (
              <span className="text-zinc-500 font-normal"> — {dlqHistoryWindowLabel}</span>
            )}
          </p>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            {dlqTasksHistory.length > 0 || dlqEventsHistory.length > 0 ? (
              <>
                <Sparkline
                  series={[
                    { values: dlqTasksHistory, color: '#f59e0b', label: 'agent-tasks DLQ' },
                    { values: dlqEventsHistory, color: '#ef4444', label: 'agent-events DLQ' },
                  ]}
                  width={600}
                  height={80}
                />
                <div className="flex gap-4 mt-2">
                  <span className="flex items-center gap-1.5 text-xs text-zinc-500">
                    <span className="w-3 h-0.5 bg-amber-400 inline-block" />
                    agent-tasks <AbbrLabel term="DLQ" />
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-zinc-500">
                    <span className="w-3 h-0.5 bg-red-400 inline-block" />
                    agent-events <AbbrLabel term="DLQ" />
                  </span>
                </div>
              </>
            ) : (
              <p className="text-xs text-zinc-500 text-center py-6">
                No history yet — depth samples accumulate on each refresh.
              </p>
            )}
          </div>
        </section>
      )}

      {/* Alert thresholds config (US-24-GR-27) */}
      <AlertThresholdsPanel onConfigSaved={setThresholdConfig} />

      {/* Alerts list (US-24-GR-27) */}
      <AlertsList refreshToken={thresholdAlertCount} />

      {/* Recent dead-letters */}
      <section aria-label="Recent dead-lettered messages">
        <p className="text-xs font-medium text-zinc-400 mb-2">
          Dead-Lettered Messages
          {deadLetterCountLabel(deadLetterDisclosure)}
        </p>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          {isLoading && !data ? (
            <div className="p-4 text-xs text-zinc-500">Loading…</div>
          ) : deadLetterDisclosure.kind === 'unreadable' ? (
            // 16357: the server said the peek threw (16305). This is the case 16237 could not
            // catch — a failed read on a queue whose depth is 0 looks exactly like good news.
            <div className="p-6 text-center text-xs text-amber-400">
              The dead-letter queue could not be read.
              {deadLetterDisclosure.shown > 0
                ? ` ${deadLetterDisclosure.shown} message${deadLetterDisclosure.shown === 1 ? '' : 's'} were retrieved before it failed; there may be more.`
                : ' Nothing was retrieved.'}{' '}
              This is a failed read, not an empty queue.
            </div>
          ) : deadLetterDisclosure.kind === 'unlistable' ? (
            // 16237: a non-zero depth beside an empty list is not an empty DLQ. Still reached when
            // the API predates 16305 and sends no flags.
            <div className="p-6 text-center text-xs text-amber-400">
              {deadLetterDisclosure.depth} dead-lettered{' '}
              {deadLetterDisclosure.depth === 1 ? 'message is' : 'messages are'} queued, but none
              could be listed. Treat this as a failed read, not an empty queue.
            </div>
          ) : deadLetterDisclosure.kind === 'empty' ? (
            // 16237: no time window is claimed, because none is applied.
            <div className="p-6 text-center text-xs text-zinc-500">
              No dead-lettered messages.
            </div>
          ) : (
            <>
              {deadLetterDisclosure.kind === 'partial' && (
                // 16237: Service Bus peeks from the front of each queue, so these are the
                // OLDEST. Saying "recent" while hiding the newest is the failure this replaces.
                <p className="px-4 pt-3 text-xs text-amber-400">
                  Showing {deadLetterDisclosure.shown} of {deadLetterDisclosure.depth}. These are
                  the oldest in each queue — newer dead letters are not listed.
                </p>
              )}
              <ul>
                {data!.recentDeadLetters.map((msg, i) => (
                  <DeadLetterRow
                    key={`${msg.deadLetteredAt}-${i}`}
                    msg={msg}
                    onRedriveClick={setRedriveTarget}
                  />
                ))}
              </ul>
            </>
          )}
        </div>
      </section>

      {/* Redrive confirmation modal */}
      {redriveTarget && (
        <DlqRedriveModal
          isOpen={true}
          queue={redriveTarget.queue}
          messageId={redriveTarget.messageId}
          deadLetterReason={redriveTarget.deadLetterReason}
          payloadPreview={redriveTarget.payloadPreview}
          onConfirm={handleConfirmRedrive}
          onCancel={() => setRedriveTarget(null)}
        />
      )}

      {/* Toast notifications */}
      <Toast toast={toast} onDismiss={handleDismissToast} />
    </main>
  );
}
